import { FC, LazyExoticComponent, ReactNode } from 'react';

import { cva, VariantProps } from 'class-variance-authority';
import { Link } from 'react-router-dom';
import type { IconType } from 'react-icons';
import { GenericIconProps } from 'ui/components/icon/Icon';
import { type Placement } from '@floating-ui/react';

import Tooltip from '../Tooltip';
import { cn } from '../../../../utils/helpers';

const iconClasses = cva([], {
  variants: {
    iconSize: {
      lg: ['w-4 h-4'],
      md: ['w-[14px] h-[14px]'],
      sm: ['w-[14px] h-[14px]'],
    },
  },
  defaultVariants: {
    iconSize: 'sm',
  },
});

const buttonClasses = cva(
  [
    'text-sm font-semibold',
    'cursor-pointer',
    'transition-all',
    'flex items-center justify-center',
    'disabled:pointer-events-none',
  ],
  {
    variants: {
      size: {
        sm: ['max-h-9 px-[18px] py-3 text-xs leading-3', 'rounded'],
        md: [
          'max-h-[44px] py-[14px] px-[22px] text-sm leading-5',
          'rounded-[6px]',
        ],
        lg: ['max-h-[52px] px-6 py-[18px] text-base', 'rounded-lg'],
      },
      intent: {
        primary: [
          'bg-primary-base text-fill-tertiary',
          'hover:bg-primary-dark',
          'disabled:bg-secondary-lighter',
        ],
        secondary: [
          'bg-secondary-dark text-fill-tertiary',
          'hover:bg-content',
          'disabled:bg-secondary-lighter',
        ],
        tertiary: [
          'outline outline-1',
          'outline-secondary-light text-secondary-dark',
          'bg-transparent',
          'hover:outline-secondary-dark',
          'disabled:outline-secondary-lighter disabled:text-disabled',
        ],
        quarternary: [
          'bg-secondary-lightest text-secondary-dark',
          'hover:bg-secondary-light',
          'disabled:bg-disabled',
          'disabled:text-fill-tertiary',
        ],
        destructive: [
          'bg-danger-base text-fill-tertiary',
          'hover:bg-danger-dark',
          'disabled:bg-disabled disabled:text-fill-tertiary',
        ],
        text: [
          'text-secondary-dark',
          'hover:text-primary-base',
          'bg-transparent',
          'disabled:text-disabled',
        ],
        link: [
          'text-info-base',
          'hover:text-info-dark',
          'bg-transparent',
          'disabled:text-disabled',
        ],
      },
    },
    defaultVariants: { intent: 'primary', size: 'sm' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonClasses> {
  label?: string | ReactNode;
  left?: boolean;
  right?: boolean;
  icon?:
    | React.ComponentType<GenericIconProps>
    | IconType
    | LazyExoticComponent<FC<GenericIconProps>>;
  expiredSubscription?: boolean;
  tooltipPosition?: Placement;
  iconClassName?: string;
}

/**
 * Button that adheres to Design System
 * @param {import('./proptypes').CustomButtonProps & import('./proptypes').ReactButtonProps} props
 * @returns {React.ReactNode}
 */

const Button: FC<ButtonProps> = ({
  size,
  intent,
  label,
  left,
  right,
  icon: Icon,
  expiredSubscription,
  tooltipPosition,
  className,
  iconClassName,
  ...props
}) => {
  if (expiredSubscription) {
    return (
      <Tooltip
        placement={tooltipPosition}
        theme="dark"
        title={
          <div className="text-center">
            <p className="font-semibold text-fill-tertiary">
              Subscribe to continue usage.
            </p>
            <Link className="font-semibold" to="/settings/billing">
              Go to billing
            </Link>
          </div>
        }
      >
        {' '}
        <button
          className={cn(buttonClasses({ size, intent, className }))}
          type="button"
          {...props}
        >
          <div className="flex gap-[11px] items-center">
            {left && Icon && (
              <Icon
                className={cn(iconClasses({ iconSize: size }), iconClassName)}
              />
            )}
            {label && <span>{label}</span>}
            {right && Icon && (
              <Icon
                className={cn(iconClasses({ iconSize: size }), iconClassName)}
              />
            )}
          </div>
        </button>
      </Tooltip>
    );
  }
  return (
    <button
      className={cn(buttonClasses({ size, intent, className }))}
      type="button"
      {...props}
    >
      <div className="flex gap-[8px] items-center">
        {left && Icon && (
          <Icon
            className={cn(iconClasses({ iconSize: size }), iconClassName)}
          />
        )}
        {label && <span>{label}</span>}
        {right && Icon && (
          <Icon
            className={cn(iconClasses({ iconSize: size }), iconClassName)}
          />
        )}
      </div>
    </button>
  );
};

export default Button;
